// Each stage's errors (the opening's, the middlegame's, the ending's), by the same rules: the reader's
// blunders (with the piece each gave away), misses, and winning positions
// thrown away.
import { type LostPiece } from '@chess/api-model';
import { type FactExtractor, type ScannedGame } from '../extractor.ts';
import { lostPieceOf } from '../lost-piece.ts';
import { type GameFacts, type StageErrors } from '@chess/api-model';

const empty = (): StageErrors => ({ blunders: 0, misses: 0, lost: [], thrown: 0, winning: false });

export const stageErrors: FactExtractor = {
  id: 'stage-errors',
  extract(game: ScannedGame, facts: GameFacts): void {
    const phaseOf = { openingErrors: 'opening', middlegame: 'middlegame', endgame: 'ending' } as const;
    const stages = (['openingErrors', 'middlegame', 'endgame'] as const).map((field) => {
      const { fromPly, toPly } = game.range(phaseOf[field]);
      return { field, fromPly, toPly, errors: toPly > fromPly ? empty() : undefined };
    });
    game.moves.forEach((move, i) => {
      const ply = i + 1;
      if (!game.isReaders(ply)) return;
      const entry = stages.find((s) => s.errors && ply > s.fromPly && ply <= s.toPly);
      const stage = entry?.errors;
      if (!entry || !stage) return;
      const before = game.pawnsAt(ply - 1);
      if (before !== undefined && before >= game.rules.winningPawns) stage.winning = true;
      const kind = game.judge.kind(ply, move, game.reader);
      if (kind === 'blunder') {
        stage.blunders += 1;
        stage.firstBlunder ??= { ply, san: move.san };
        const after = game.at(ply);
        const lost: LostPiece | undefined = after ? lostPieceOf(after.fen, after.lines[0], after.score, game.reader) : undefined;
        if (lost) stage.lost.push(lost);
      } else if (kind === 'miss') {
        stage.misses += 1;
      }
      // A won ending counts as lost only when the reader lost the game; the
      // opening's and middlegame's advantage thrown away keep to the move, whatever the result.
      if (kind && game.threw(ply) && (entry.field !== 'endgame' || facts.outcome === 'loss')) stage.thrown += 1;
    });
    for (const s of stages) if (s.errors) facts[s.field] = s.errors;
  },
};
